/**
 * Pure helpers for the road-blockages map overlay: region resolution, geometry
 * point extraction, and label formatting. Kept side-effect free so they can be
 * unit-tested in isolation from the MapLibre layer wiring.
 */
import { regionNameToId } from '@backoffice-frontend/service-area-picker'

/**
 * Regions custom-road-blockages supports that aren't in
 * region-servicearea-mapping.json, kept here instead of added to that file.
 *
 * That file is periodically hand-synced to match the maps repo's canonical
 * copy (e.g. #14034) by replacing its entire `regions` array wholesale, not
 * merging — anything added here that isn't also in the maps repo's copy would
 * get silently deleted next sync. That's exactly how these two regions were
 * lost the first time: they were in a local REGION_IDS table that got
 * replaced with this shared file, which never had them.
 *
 * They also don't fit that file's schema like the other regions do — every
 * entry there has a `service_areas` array backed by real production service
 * areas, which these two don't have yet. custom-road-blockages treats LA as
 * an interim workaround (service area UUID == regionId, see
 * MapServiceAreaToRegion.kt), and London only shows up elsewhere as a
 * taxi-mode routing test region, not a provisioned ridepooling region.
 *
 * If these become fully supported regions, add them to the maps repo's copy
 * first and let it flow down — that's a call for the maps/region-server team,
 * not something to bake into a single feature's lookup.
 */
const SUPPLEMENTAL_REGION_IDS: Record<string, string> = {
  'los-angeles': 'ceab2d3c-4498-4ae2-8351-bcc83f0eee85',
  london: '9bb27625-b6f6-4b82-9614-5a0e687be0a0',
}

/**
 * Resolve a metropolis display name (e.g. "Lake Nona") to its region ID by
 * normalizing to the lowercase, dash-separated form the mapping keys on.
 */
export const resolveRegionId = (
  metropolisName: string | undefined,
): string | undefined => {
  if (!metropolisName) return undefined
  const key = metropolisName.toLowerCase().replace(/\s+/g, '-')
  return regionNameToId[key] ?? SUPPLEMENTAL_REGION_IDS[key]
}

/** Flatten a Line/MultiLineString geometry into a single coordinate list. */
const flattenCoordinates = (geometry: GeoJSON.Geometry): number[][] => {
  if (geometry.type === 'LineString') return geometry.coordinates
  if (geometry.type === 'MultiLineString') return geometry.coordinates.flat()
  return []
}

const pointFeature = (
  coordinates: number[],
): GeoJSON.Feature<GeoJSON.Point> => ({
  type: 'Feature',
  properties: {},
  geometry: { type: 'Point', coordinates },
})

/** Extract endpoint coordinates (first and last) from a GeoJSON geometry. */
export const extractEndpoints = (
  geometry: GeoJSON.Geometry,
): GeoJSON.Feature<GeoJSON.Point>[] => {
  const coords = flattenCoordinates(geometry)
  if (coords.length < 2) return []
  return [pointFeature(coords[0]), pointFeature(coords[coords.length - 1])]
}

/**
 * Find the point halfway along a geometry's length. Used to place the barrier
 * icon as a standalone point — `symbol-placement: 'line-center'` renders the
 * icon twice, and a point symbol also keeps it reliably upright.
 */
export const extractCenter = (
  geometry: GeoJSON.Geometry,
): GeoJSON.Feature<GeoJSON.Point> | null => {
  const coords = flattenCoordinates(geometry)
  if (coords.length < 2) return null

  const segmentLengths: number[] = []
  let totalLength = 0
  for (let i = 1; i < coords.length; i++) {
    const [x1, y1] = coords[i - 1]
    const [x2, y2] = coords[i]
    const length = Math.hypot(x2 - x1, y2 - y1)
    segmentLengths.push(length)
    totalLength += length
  }

  let remaining = totalLength / 2
  for (let i = 0; i < segmentLengths.length; i++) {
    if (remaining <= segmentLengths[i]) {
      const t = segmentLengths[i] === 0 ? 0 : remaining / segmentLengths[i]
      const [x1, y1] = coords[i]
      const [x2, y2] = coords[i + 1]
      return pointFeature([x1 + (x2 - x1) * t, y1 + (y2 - y1) * t])
    }
    remaining -= segmentLengths[i]
  }

  return pointFeature(coords[coords.length - 1])
}

/**
 * Turn a raw blockage reason enum value (e.g. "ROAD_WORKS" or "roadWorks") into
 * a human-readable, sentence-cased label ("Road works").
 */
export const formatReason = (reason: string): string =>
  reason
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase())
